import { fmtDate } from "@/lib/dates";
import { cn } from "@/lib/utils";

// Long codes and long titles wrap inside their cell rather than widening the table past a phone.
const WRAP = "[overflow-wrap:anywhere]";
const INK = "border border-neutral-800";

const Blank = () => <span className="text-neutral-400">—</span>;

/**
 * The top of a printed form, as the PDF lays it out: the mark, the company over the form's title,
 * and the document-control box (Doc. No., Rev. No., Effective Date.) — Shakun's own forms carry
 * exactly this block, so HR can check it against the paper they file beside it.
 *
 * Ink on paper in both themes: this previews a printed sheet, and theme colours would preview a
 * document that does not exist.
 */
export default function DocumentHeaderPreview({ companyName, title, docNo, revNo, effectiveDate, footerNote, className }) {
  const note = (footerNote || "").trim();
  return (
    <div className={cn("rounded-md border border-border bg-white p-2.5 text-neutral-900 shadow-sm sm:p-4", className)}
         data-testid="doc-preview">
      <table className={cn("w-full table-fixed border-collapse", INK)}>
        <tbody>
          <tr>
            <td rowSpan={3} className={cn(INK, "w-[17%] p-1 text-center align-middle sm:p-2")}>
              <img src="/brand/shakun-mark.png" alt="" aria-hidden="true"
                   className="mx-auto h-9 w-auto sm:h-14" width="40" height="56" />
            </td>
            <td rowSpan={3} className={cn(INK, WRAP, "px-1.5 py-1.5 text-center align-middle sm:px-3")}>
              <div className="text-[10px] font-bold leading-tight sm:text-sm" data-testid="doc-preview-company">
                {companyName?.trim() || <Blank />}
              </div>
              <div className="mt-1 text-[10px] font-bold leading-snug underline decoration-dotted underline-offset-2 sm:mt-2 sm:text-[15px]"
                   data-testid="doc-preview-title">
                {title?.trim() || <Blank />}
              </div>
            </td>
            <td className={cn(INK, "w-[21%] px-1 py-1 text-[9px] font-semibold leading-tight sm:w-[19%] sm:px-2 sm:text-xs")}>Doc. No.</td>
            <td className={cn(INK, WRAP, "w-[19%] px-1 py-1 text-center text-[9px] font-semibold leading-tight sm:w-[17%] sm:px-2 sm:text-xs")}
                data-testid="doc-preview-doc-no">
              {docNo?.trim() || <Blank />}
            </td>
          </tr>
          <tr>
            <td className={cn(INK, "px-1 py-1 text-[9px] font-semibold leading-tight sm:px-2 sm:text-xs")}>Rev. No.</td>
            <td className={cn(INK, WRAP, "px-1 py-1 text-center text-[9px] font-semibold leading-tight sm:px-2 sm:text-xs")}
                data-testid="doc-preview-rev-no">
              {revNo?.trim() || <Blank />}
            </td>
          </tr>
          <tr>
            <td className={cn(INK, "px-1 py-1 text-[9px] font-semibold leading-tight sm:px-2 sm:text-xs")}>Effective Date.</td>
            <td className={cn(INK, "px-1 py-1 text-center text-[9px] font-semibold leading-tight sm:px-2 sm:text-xs")}
                data-testid="doc-preview-effective">
              {effectiveDate ? fmtDate(effectiveDate) : <Blank />}
            </td>
          </tr>
        </tbody>
      </table>
      {note && (
        <div className="mt-3 flex flex-wrap items-end gap-x-3 gap-y-1">
          <div className={cn(INK, WRAP, "max-w-full whitespace-pre-line px-2 py-1 text-[9px] leading-snug sm:text-xs")}
               data-testid="doc-preview-footer">
            {note}
          </div>
          <span className="text-[10px] text-neutral-500">Bottom left of every page</span>
        </div>
      )}
    </div>
  );
}
